import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect } from "react";
import appCss from "../styles.css?url";
import { AuthProvider } from "@/lib/auth/AuthContext";
import { installBrowserErrorReporting, reportLovableError } from "@/lib/lovable-error-reporting";
import { clearStaleModuleGuard, isStaleModuleError, reloadOnceForStaleModules } from "@/lib/stale-module-recovery";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 grid-bg">
      <div className="glass max-w-md text-center p-10 rounded-2xl">
        <p className="font-mono text-xs text-cyan tracking-[0.3em]">Page not found</p>
        <h1 className="mt-3 font-display text-6xl font-semibold text-glow">404</h1>
        <p className="mt-3 text-sm text-muted-foreground">There's nothing at this address. It may have moved, or the link is out of date.</p>
        <Link to="/dashboard" className="mt-6 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90">
          Go to your dashboard
        </Link>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  const msg = error?.message || "";
  const isChunkError = isStaleModuleError(msg);
  useEffect(() => {
    if (!isChunkError) {
      reportLovableError(error, { boundary: "tanstack_root_error_component" });
      return;
    }
    reloadOnceForStaleModules();
  }, [isChunkError, error]);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="glass max-w-md text-center p-8 rounded-2xl">
        <p className="font-mono text-xs text-amber tracking-[0.3em]">SYSTEM_FAULT</p>
        <h1 className="mt-3 font-display text-2xl">{isChunkError ? "Updating console…" : "Pipeline interrupted"}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{isChunkError ? "A new build is available. Reloading…" : msg}</p>
        <div className="mt-6 flex justify-center gap-2">
          <button
            onClick={() => {
              if (isChunkError && typeof window !== "undefined") { window.location.reload(); return; }
              router.invalidate(); reset();
            }}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >
            {isChunkError ? "Reload now" : "Retry collection"}
          </button>
          <a href="/dashboard" className="rounded-md border border-panel-border bg-panel/40 px-4 py-2 text-sm">Command</a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { title: "CARIO — OSINT research in one workspace" },
      { name: "description", content: "Search a name, email, domain, phone or wallet across hundreds of public sources and keep every citation." },
      { name: "author", content: "CARIO INTEL" },
      { property: "og:title", content: "CARIO — OSINT research in one workspace" },
      { property: "og:description", content: "Search a name, email, domain, phone or wallet across hundreds of public sources and keep every citation." },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "CARIO — OSINT research in one workspace" },
      { name: "twitter:description", content: "Search a name, email, domain, phone or wallet across hundreds of public sources and keep every citation." },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/e9435844-45fb-4a82-b228-1725aa8ab98b" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/e9435844-45fb-4a82-b228-1725aa8ab98b" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&family=Space+Grotesk:wght@500;600;700&display=swap" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://carionexus.com/#org",
              name: "CARIO INTEL",
              url: "https://carionexus.com",
              description: "Search public records, registries, breaches and news from one place, and keep the sources.",
              logo: "https://carionexus.com/favicon.ico",
            },
            {
              "@type": "WebSite",
              "@id": "https://carionexus.com/#website",
              url: "https://carionexus.com",
              name: "CARIO INTEL",
              publisher: { "@id": "https://carionexus.com/#org" },
              potentialAction: {
                "@type": "SearchAction",
                target: "https://carionexus.com/dashboard?q={search_term_string}",
                "query-input": "required name=search_term_string",
              },
            },
            {
              "@type": "SoftwareApplication",
              name: "CARIO INTEL",
              applicationCategory: "SecurityApplication",
              operatingSystem: "Web",
              offers: {
                "@type": "AggregateOffer",
                lowPrice: "29",
                highPrice: "599",
                priceCurrency: "USD",
                offerCount: "3",
              },
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head><HeadContent /></head>
      <body>{children}<Scripts /></body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  useEffect(() => {
    installBrowserErrorReporting();
    // The shell rendered — any earlier chunk-mismatch reload succeeded.
    clearStaleModuleGuard();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Outlet />
      </AuthProvider>
    </QueryClientProvider>
  );
}
